/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetTipiComunicazioneParams,
  TipoComunicazioneDTO,
  TipoComunicazioneRequest,
  WsProblema,
  WsRispostaPaginataListTipoComunicazioneDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ottiene i dettagli di un tipo comunicazione
 * @summary Dettaglio Tipo comunicazione
 */
export const getTipiComunicazioneIdComunicazione = (
  idComunicazione: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoComunicazioneDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-comunicazione/${idComunicazione}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiComunicazioneIdComunicazioneQueryKey = (
  idComunicazione: string,
) =>
  [
    `/framework-cfg-procedimenti/tipi-comunicazione/${idComunicazione}`,
  ] as const;

export const getGetTipiComunicazioneIdComunicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazione>>,
  TError = ErrorType<WsProblema>,
>(
  idComunicazione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTipiComunicazioneIdComunicazioneQueryKey(idComunicazione);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazione>>
  > = ({ signal }) =>
    getTipiComunicazioneIdComunicazione(
      idComunicazione,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idComunicazione, ...queryOptions };
};

export type GetTipiComunicazioneIdComunicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazione>>
>;
export type GetTipiComunicazioneIdComunicazioneQueryError =
  ErrorType<WsProblema>;

/**
 * @summary Dettaglio Tipo comunicazione
 */
export const useGetTipiComunicazioneIdComunicazione = <
  TData = Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazione>>,
  TError = ErrorType<WsProblema>,
>(
  idComunicazione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiComunicazioneIdComunicazioneQueryOptions(
    idComunicazione,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un tipo comunicazione
 * @summary Aggiorna un tipo comunicazione
 */
export const putTipiComunicazioneIdComunicazione = (
  idComunicazione: string,
  tipoComunicazioneRequest: TipoComunicazioneRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoComunicazioneDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-comunicazione/${idComunicazione}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tipoComunicazioneRequest,
    },
    options,
  );
};

export const getPutTipiComunicazioneIdComunicazioneMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiComunicazioneIdComunicazione>>,
    TError,
    { idComunicazione: string; data: TipoComunicazioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiComunicazioneIdComunicazione>>,
  TError,
  { idComunicazione: string; data: TipoComunicazioneRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiComunicazioneIdComunicazione>>,
    { idComunicazione: string; data: TipoComunicazioneRequest }
  > = props => {
    const { idComunicazione, data } = props ?? {};

    return putTipiComunicazioneIdComunicazione(
      idComunicazione,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiComunicazioneIdComunicazioneMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiComunicazioneIdComunicazione>>
>;
export type PutTipiComunicazioneIdComunicazioneMutationBody =
  TipoComunicazioneRequest;
export type PutTipiComunicazioneIdComunicazioneMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Aggiorna un tipo comunicazione
 */
export const usePutTipiComunicazioneIdComunicazione = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiComunicazioneIdComunicazione>>,
    TError,
    { idComunicazione: string; data: TipoComunicazioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutTipiComunicazioneIdComunicazioneMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove un tipo comunicazione
 * @summary Elimina un tipo comunicazione
 */
export const deleteTipiComunicazioneIdComunicazione = (
  idComunicazione: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-procedimenti/tipi-comunicazione/${idComunicazione}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteTipiComunicazioneIdComunicazioneMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiComunicazioneIdComunicazione>>,
    TError,
    { idComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipiComunicazioneIdComunicazione>>,
  TError,
  { idComunicazione: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipiComunicazioneIdComunicazione>>,
    { idComunicazione: string }
  > = props => {
    const { idComunicazione } = props ?? {};

    return deleteTipiComunicazioneIdComunicazione(
      idComunicazione,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipiComunicazioneIdComunicazioneMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipiComunicazioneIdComunicazione>>
>;

export type DeleteTipiComunicazioneIdComunicazioneMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Elimina un tipo comunicazione
 */
export const useDeleteTipiComunicazioneIdComunicazione = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiComunicazioneIdComunicazione>>,
    TError,
    { idComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteTipiComunicazioneIdComunicazioneMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco dei tipi comunicazione paginati ordinati per codice
 * @summary Elenco Tipi Comunicazione
 */
export const getTipiComunicazione = (
  params?: GetTipiComunicazioneParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTipoComunicazioneDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-comunicazione`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiComunicazioneQueryKey = (
  params?: GetTipiComunicazioneParams,
) =>
  [
    `/framework-cfg-procedimenti/tipi-comunicazione`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiComunicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiComunicazione>>,
  TError = ErrorType<WsProblema>,
>(
  params?: GetTipiComunicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiComunicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiComunicazioneQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiComunicazione>>
  > = ({ signal }) => getTipiComunicazione(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiComunicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiComunicazione>>
>;
export type GetTipiComunicazioneQueryError = ErrorType<WsProblema>;

/**
 * @summary Elenco Tipi Comunicazione
 */
export const useGetTipiComunicazione = <
  TData = Awaited<ReturnType<typeof getTipiComunicazione>>,
  TError = ErrorType<WsProblema>,
>(
  params?: GetTipiComunicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiComunicazioneQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Inserisce un tipo comunicazione
 * @summary Inserisce un tipo comunicazione
 */
export const postTipiComunicazione = (
  tipoComunicazioneRequest: TipoComunicazioneRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoComunicazioneDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-comunicazione`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tipoComunicazioneRequest,
    },
    options,
  );
};

export const getPostTipiComunicazioneMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiComunicazione>>,
    TError,
    { data: TipoComunicazioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiComunicazione>>,
  TError,
  { data: TipoComunicazioneRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiComunicazione>>,
    { data: TipoComunicazioneRequest }
  > = props => {
    const { data } = props ?? {};

    return postTipiComunicazione(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiComunicazioneMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiComunicazione>>
>;
export type PostTipiComunicazioneMutationBody = TipoComunicazioneRequest;
export type PostTipiComunicazioneMutationError = ErrorType<WsProblema>;

/**
 * @summary Inserisce un tipo comunicazione
 */
export const usePostTipiComunicazione = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiComunicazione>>,
    TError,
    { data: TipoComunicazioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiComunicazioneMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Controlla se esistono istande di comunicazioni per il tipo comunicazione passato. Se esistono è possibile modificare solo oggetto e messaggio del tipo comunicazione
 * @summary Controlla se esistono istande di comunicazioni per il tipo comunicazione passato
 */
export const getTipiComunicazioneIdComunicazioneExistIstanze = (
  idComunicazione: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<boolean>(
    {
      url: `/framework-cfg-procedimenti/tipi-comunicazione/${idComunicazione}/exist-istanze`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiComunicazioneIdComunicazioneExistIstanzeQueryKey = (
  idComunicazione: string,
) =>
  [
    `/framework-cfg-procedimenti/tipi-comunicazione/${idComunicazione}/exist-istanze`,
  ] as const;

export const getGetTipiComunicazioneIdComunicazioneExistIstanzeQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getTipiComunicazioneIdComunicazioneExistIstanze>
  >,
  TError = ErrorType<WsProblema>,
>(
  idComunicazione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getTipiComunicazioneIdComunicazioneExistIstanze>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazioneExistIstanze>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTipiComunicazioneIdComunicazioneExistIstanzeQueryKey(idComunicazione);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazioneExistIstanze>>
  > = ({ signal }) =>
    getTipiComunicazioneIdComunicazioneExistIstanze(
      idComunicazione,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idComunicazione, ...queryOptions };
};

export type GetTipiComunicazioneIdComunicazioneExistIstanzeQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getTipiComunicazioneIdComunicazioneExistIstanze>>
  >;
export type GetTipiComunicazioneIdComunicazioneExistIstanzeQueryError =
  ErrorType<WsProblema>;

/**
 * @summary Controlla se esistono istande di comunicazioni per il tipo comunicazione passato
 */
export const useGetTipiComunicazioneIdComunicazioneExistIstanze = <
  TData = Awaited<
    ReturnType<typeof getTipiComunicazioneIdComunicazioneExistIstanze>
  >,
  TError = ErrorType<WsProblema>,
>(
  idComunicazione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getTipiComunicazioneIdComunicazioneExistIstanze>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetTipiComunicazioneIdComunicazioneExistIstanzeQueryOptions(
      idComunicazione,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene i dettagli di un tipo comunicazione tramite il codice
 * @summary Dettaglio Tipo comunicazione da codice
 */
export const getTipiComunicazioneCodiceCode = (
  code: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoComunicazioneDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-comunicazione/codice/${code}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiComunicazioneCodiceCodeQueryKey = (code: string) =>
  [`/framework-cfg-procedimenti/tipi-comunicazione/codice/${code}`] as const;

export const getGetTipiComunicazioneCodiceCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiComunicazioneCodiceCode>>,
  TError = ErrorType<WsProblema>,
>(
  code: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiComunicazioneCodiceCode>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiComunicazioneCodiceCode>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiComunicazioneCodiceCodeQueryKey(code);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiComunicazioneCodiceCode>>
  > = ({ signal }) =>
    getTipiComunicazioneCodiceCode(code, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!code, ...queryOptions };
};

export type GetTipiComunicazioneCodiceCodeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiComunicazioneCodiceCode>>
>;
export type GetTipiComunicazioneCodiceCodeQueryError = ErrorType<WsProblema>;

/**
 * @summary Dettaglio Tipo comunicazione da codice
 */
export const useGetTipiComunicazioneCodiceCode = <
  TData = Awaited<ReturnType<typeof getTipiComunicazioneCodiceCode>>,
  TError = ErrorType<WsProblema>,
>(
  code: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiComunicazioneCodiceCode>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiComunicazioneCodiceCodeQueryOptions(
    code,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
